import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import RiyalSymbol from '../components/RiyalSymbol';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import '../styles/PromoCodesManagement.css';

interface PromoCode {
  _id: string;
  code: string;
  description?: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxUses?: number;
  currentUses: number;
  minPurchaseAmount?: number;
  validFrom: string;
  validUntil?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function PromoCodesManagement() {
  const navigate = useNavigate();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: 0,
    maxUses: '',
    minPurchaseAmount: 0,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    isActive: true
  });

  useEffect(() => {
    const token = localStorage.getItem('fotomista_token') || localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPromoCodes();
  }, [navigate]);

  const fetchPromoCodes = async () => {
    try {
      const token = localStorage.getItem('fotomista_token') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROMO_CODES_ADMIN_ALL}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data.promoCodes || []);
      }
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('fotomista_token') || localStorage.getItem('adminToken');
      const url = editingPromo 
        ? `${API_BASE_URL}${API_ENDPOINTS.PROMO_CODES_ADMIN_UPDATE}/${editingPromo._id}`
        : `${API_BASE_URL}${API_ENDPOINTS.PROMO_CODES_ADMIN_CREATE}`;
      
      const method = editingPromo ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
          validUntil: formData.validUntil || undefined
        })
      });

      if (response.ok) {
        fetchPromoCodes();
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();
        alert(editingPromo ? 'تم تحديث كود الخصم بنجاح' : 'تم إنشاء كود الخصم بنجاح');
      } else {
        const data = await response.json();
        alert(data.message || 'حدث خطأ');
      }
    } catch (error) {
      console.error('Error saving promo code:', error);
      alert('حدث خطأ في حفظ كود الخصم');
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      const token = localStorage.getItem('fotomista_token') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROMO_CODES_ADMIN_TOGGLE}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchPromoCodes();
      }
    } catch (error) {
      console.error('Error toggling promo code:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('هل أنت متأكد من حذف كود الخصم؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('fotomista_token') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROMO_CODES_ADMIN_DELETE}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchPromoCodes();
        alert('تم حذف كود الخصم بنجاح');
      }
    } catch (error) {
      console.error('Error deleting promo code:', error);
      alert('حدث خطأ في حذف كود الخصم');
    }
  };

  const handleEdit = (promo: PromoCode) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      description: promo.description || '',
      discountType: promo.discountType,
      discountValue: promo.discountValue,
      maxUses: promo.maxUses?.toString() || '',
      minPurchaseAmount: promo.minPurchaseAmount || 0,
      validFrom: new Date(promo.validFrom).toISOString().split('T')[0],
      validUntil: promo.validUntil ? new Date(promo.validUntil).toISOString().split('T')[0] : '',
      isActive: promo.isActive
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: 0,
      maxUses: '',
      minPurchaseAmount: 0,
      validFrom: new Date().toISOString().split('T')[0],
      validUntil: '',
      isActive: true
    });
    setEditingPromo(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (promo: PromoCode) => {
    const now = new Date();
    const validUntil = promo.validUntil ? new Date(promo.validUntil) : null;
    
    if (!promo.isActive) {
      return <span className="badge badge-inactive">غير نشط</span>;
    }
    
    if (validUntil && validUntil < now) {
      return <span className="badge badge-expired">منتهي</span>;
    }
    
    if (promo.maxUses && promo.currentUses >= promo.maxUses) {
      return <span className="badge badge-used">مستنفذ</span>;
    }
    
    return <span className="badge badge-active">نشط</span>;
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <AdminLayout title="إدارة أكواد الخصم">
      <motion.div 
        className="promo-codes-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="actions-bar">
          <button 
            className="add-promo-btn"
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          >
            + إضافة كود خصم جديد
          </button>
        </div>

        <div className="promo-codes-grid">
          {promoCodes.length === 0 ? (
            <div className="no-promos">
              <p>لا توجد أكواد خصم حالياً</p>
            </div>
          ) : (
            promoCodes.map(promo => (
              <div key={promo._id} className="promo-card">
                <div className="promo-header">
                  <h3>{promo.code}</h3>
                  {getStatusBadge(promo)}
                </div>
                
                {promo.description && (
                  <p className="promo-description">{promo.description}</p>
                )}
                
                <div className="promo-details">
                  <div className="detail-item">
                    <span className="label">نوع الخصم:</span>
                    <span className="value">
                      {promo.discountType === 'percentage' ? 'نسبة مئوية' : 'مبلغ ثابت'}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="label">قيمة الخصم:</span>
                    <span className="value">
                      {promo.discountType === 'percentage' 
                        ? `${promo.discountValue}%`
                        : (
                          <>
                            {promo.discountValue}
                            <RiyalSymbol size={14} />
                          </>
                        )
                      }
                    </span>
                  </div>
                  
                  {promo.minPurchaseAmount && promo.minPurchaseAmount > 0 && (
                    <div className="detail-item">
                      <span className="label">الحد الأدنى للشراء:</span>
                      <span className="value">
                        {promo.minPurchaseAmount}
                        <RiyalSymbol size={14} />
                      </span>
                    </div>
                  )}
                  
                  <div className="detail-item">
                    <span className="label">الاستخدامات:</span>
                    <span className="value">
                      {promo.currentUses}
                      {promo.maxUses && ` / ${promo.maxUses}`}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="label">صالح من:</span>
                    <span className="value">{formatDate(promo.validFrom)}</span>
                  </div>
                  
                  {promo.validUntil && (
                    <div className="detail-item">
                      <span className="label">صالح حتى:</span>
                      <span className="value">{formatDate(promo.validUntil)}</span>
                    </div>
                  )}
                </div>
                
                <div className="promo-actions">
                  <button 
                    className="edit-btn"
                    onClick={() => handleEdit(promo)}
                  >
                    تعديل
                  </button>
                  <button 
                    className={`toggle-btn ${promo.isActive ? 'active' : 'inactive'}`}
                    onClick={() => handleToggleActive(promo._id)}
                  >
                    {promo.isActive ? 'إيقاف' : 'تفعيل'}
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(promo._id)}
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

      {/* Modal for Add/Edit */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPromo ? 'تعديل كود الخصم' : 'إضافة كود خصم جديد'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>كود الخصم *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  required
                  placeholder="مثال: SAVE20"
                />
              </div>
              
              <div className="form-group">
                <label>الوصف</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="وصف كود الخصم (اختياري)"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>نوع الخصم *</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({...formData, discountType: e.target.value as 'percentage' | 'fixed'})}
                  >
                    <option value="percentage">نسبة مئوية</option>
                    <option value="fixed">مبلغ ثابت</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>قيمة الخصم *</label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({...formData, discountValue: parseFloat(e.target.value) || 0})}
                    min="0"
                    step={formData.discountType === 'percentage' ? '1' : '0.01'}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>الحد الأدنى للشراء</label>
                  <input
                    type="number"
                    value={formData.minPurchaseAmount}
                    onChange={(e) => setFormData({...formData, minPurchaseAmount: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div className="form-group">
                  <label>الحد الأقصى للاستخدامات</label>
                  <input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData({...formData, maxUses: e.target.value})}
                    min="1"
                    placeholder="غير محدود"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>صالح من *</label>
                  <input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData({...formData, validFrom: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>صالح حتى</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
                    min={formData.validFrom}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  />
                  كود نشط
                </label>
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="save-btn">
                  {editingPromo ? 'حفظ التغييرات' : 'إضافة الكود'}
                </button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </motion.div>
    </AdminLayout>
  );
}

export default PromoCodesManagement;